import { http } from "../http";

/**
 * deleted är en mjuk radering. Hård radering vore verkningslös för
 * importerade ställen — nästa import skulle skapa raden igen.
 */
export type VenueStatus = "active" | "needs_review" | "hidden" | "deleted";

/** Betalande kund eller inte. Bara premium får live-data på kartan. */
export type VenuePlan = "free" | "premium";

/** Ett pass. close är null när källan inte vet när det stänger. */
export type OpenInterval = { open: string; close: string | null };

/** Nycklarna är mon..sun. Tom lista betyder stängt den dagen. */
export type OpenHours = Record<string, OpenInterval[]>;

export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

export const WEEKDAY_LABELS: Record<string, string> = {
  mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun",
};

/**
 * Hur sensorerna på ett venue spelar in och mäter. Ställs bara här, under
 * Edit venue, och gäller alla sensorer på stället från nästa heartbeat.
 * Speglar sensorSettings.js i API:t.
 */
export type VenueSensorSettings = {
  recordSeconds: number;
  intervalSeconds: number;
  activity: {
    enabled: boolean;
    intervalSeconds: number;
    wifiEnabled: boolean;
    bleEnabled: boolean;
    audioEnabled: boolean;
    wifiSampleSeconds: number;
    bleSampleSeconds: number;
    wifiMinRssi: number;
    bleMinRssi: number;
  };
};

/** Samma gränser som servern och firmwaren. Servern avgör ändå. */
export const SENSOR_SETTING_LIMITS = {
  recordSeconds: { min: 3, max: 30 },
  intervalSeconds: { min: 15, max: 3600 },
  activityIntervalSeconds: { min: 5, max: 3600 },
  wifiSampleSeconds: { min: 1, max: 30 },
  bleSampleSeconds: { min: 1, max: 15 },
  minRssi: { min: -100, max: -20 },
};

/** Hur länge en sensor räknas som online efter senaste heartbeat. */
export function sensorOnlineWindowMs(intervalSeconds = 60) {
  return Math.max(120_000, intervalSeconds * 2_000 + 15_000);
}

export type CreateVenueInput = {
  name: string;
  lat?: number | null;
  lon?: number | null;
  address?: string;
  imageUrl?: string;
  timezone?: string;
  openHoursJson?: string;
};

export type VenueType = {
  id: string;
  name: string;
  imageUrl?: string | null;
  address?: string | null;
  lat?: number | null;
  lon?: number | null;
  timezone?: string | null;
  city?: string | null;
  openHoursJson?: string | null;

  source?: string | null;
  sourceId?: string | null;
  sourceUpdatedAt?: string | null;
  venueType?: string | null;
  status: VenueStatus;
  plan?: VenuePlan;
  website?: string | null;
  phone?: string | null;

  /** Originalsträngen från källan, i OSM:s opening_hours-syntax. */
  openHoursRaw?: string | null;

  createdAt?: string;
  sensorSettings: VenueSensorSettings;
  sensors: SensorType[];
};

export type SensorType = {
  id: string;
  sensorId: string;
  name?: string | null;
  /** Installerarens namn på lådan. name är samma värde, kvar för äldre läsare. */
  installationName?: string | null;
  venueId?: string | null;
  isActive: boolean;
  lastSeenAt?: string | null;
  lastStatus?: string | null;
};

export async function createVenue(data: CreateVenueInput) {
  return http.post("/admin/venues", data);
}

export async function updateVenue(id: string, data: CreateVenueInput) {
  return http.put(`/admin/venues/${encodeURIComponent(id)}`, data);
}

export async function deleteVenue(id: string) {
  return http.delete(`/admin/venues/${encodeURIComponent(id)}`);
}

export type VenuesPage = {
  total: number;
  limit: number;
  offset: number;
  items: VenueType[];
};

export type VenueQuery = {
  q?: string;
  status?: VenueStatus | "all";
  venueType?: string;
  city?: string;
  limit?: number;
  offset?: number;
};

export type CityCount = { city: string; count: number };

/** Städerna som faktiskt finns i datan, för filtrets rullgardin. */
export async function getVenueCities() {
  return http.get<{ items: CityCount[]; withoutCity: number }>(
    "/admin/venues/cities"
  );
}

/**
 * Listan är paginerad och filtren körs i databasen. Efter en OSM-import
 * finns tusentals rader, och att hämta alla i ett svar är både långsamt och
 * onödigt.
 */
export async function getVenues(query: VenueQuery = {}) {
  const params = new URLSearchParams();

  if (query.q) params.set("q", query.q);
  if (query.status && query.status !== "all") params.set("status", query.status);
  if (query.venueType && query.venueType !== "all") params.set("venueType", query.venueType);
  if (query.city && query.city !== "all") params.set("city", query.city);
  params.set("limit", String(query.limit ?? 100));
  params.set("offset", String(query.offset ?? 0));

  return http.get<VenuesPage>(`/admin/venues?${params.toString()}`);
}

export async function updateVenueStatus(id: string, status: VenueStatus) {
  return http.patch(`/admin/venues/${encodeURIComponent(id)}/status`, {
    status,
  });
}

export async function updateSensorActive(sensorId: string, isActive: boolean) {
  return http.patch(`/admin/sensors/${encodeURIComponent(sensorId)}`, {
    isActive: Boolean(isActive),
  });
}

/** Intervallen ställs på venuet (patchVenue, sensorSettings), inte här. */
export type UpdateSensorInput = {
  venueId?: string | null;
  isActive?: boolean;
};

export async function updateSensor(sensorId: string, data: UpdateSensorInput) {
  return http.patch(`/admin/sensors/${encodeURIComponent(sensorId)}`, data);
}

// ------------------------------------------------------------------
// Redigering
// ------------------------------------------------------------------

export type VenuePatch = {
  name?: string;
  address?: string;
  website?: string;
  phone?: string;
  venueType?: string;
  timezone?: string;
  city?: string;
  lat?: number | null;
  lon?: number | null;
  status?: VenueStatus;
  plan?: VenuePlan;
  /**
   * Skickas som objekt, inte sträng. Går inte att nollställa: servern
   * kräver minst ett pass, eftersom sensorn inte kör utan öppettider.
   */
  openHours?: OpenHours;
  sensorSettings?: Partial<Omit<VenueSensorSettings, "activity">> & {
    activity?: Partial<VenueSensorSettings["activity"]>;
  };
};

/** Bara fält som skickas med ändras, resten lämnas orörda. */
export async function patchVenue(id: string, data: VenuePatch) {
  return http.patch<{ ok: boolean; venue: VenueType }>(
    `/admin/venues/${encodeURIComponent(id)}`,
    data
  );
}

/** Tolkar den lagrade JSON-strängen. Tom vecka när fältet saknas. */
export function parseOpenHours(raw?: string | null): OpenHours {
  if (!raw) return emptyOpenHours();

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return emptyOpenHours();

    const week = emptyOpenHours();
    for (const day of WEEKDAYS) {
      if (Array.isArray(parsed[day])) week[day] = parsed[day];
    }
    return week;
  } catch {
    return emptyOpenHours();
  }
}

export function emptyOpenHours(): OpenHours {
  return Object.fromEntries(WEEKDAYS.map((d) => [d, []]));
}

// ------------------------------------------------------------------
// Omseedning
// ------------------------------------------------------------------

export type ImportJob = {
  id: string;
  kind: string;
  status: "queued" | "running" | "done" | "failed";
  created: number;
  updated: number;
  skipped: number;
  total: number;
  message?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
  createdAt: string;
};

export type Bbox = { south: number; west: number; north: number; east: number };

export type GeocodeHit = {
  name: string;
  city: string;
  type: string | null;
  bbox: Bbox;
  /** True när området är för stort för Overpass och måste delas upp. */
  tooLarge: boolean;
};

/** Slår upp en ort och ger dess bounding box. */
export async function geocodeCity(q: string) {
  const res = await http.get<{ items: GeocodeHit[] }>(
    `/admin/venues/geocode?q=${encodeURIComponent(q)}`
  );
  return res.items ?? [];
}

export async function startVenueImport(params?: { bbox?: Bbox; city?: string }) {
  return http.post<{ ok: boolean; jobId: string }>(
    "/admin/venues/import",
    params ?? {}
  );
}

export async function getImportJob(id: string) {
  const res = await http.get<{ job: ImportJob }>(
    `/admin/import-jobs/${encodeURIComponent(id)}`
  );
  return res.job;
}

export async function getImportJobs() {
  const res = await http.get<{ items: ImportJob[] }>("/admin/import-jobs");
  return res.items;
}
